<template>
  <el-dialog
    :visible.sync="visible"
    width="1060px"
    height="680px"
    :close-on-click-modal="false"
    custom-class="privacy-dialog"
    :before-close="handleClose"
    :show-close="true"
  >
    <span slot="title" style="color:#3855F1; font-family: 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif; font-size:22px; font-weight:600; line-height:normal;">Privacy Policy for Indonesia — Personal Users' Version</span>
    <div class="privacy-policy-page">
      <div class="container">
        <h3>1. Introduction</h3>
        <p>PayCools HongKong Limited ("Company", "we", "us", or "our") is committed to protecting and respecting your privacy. This Privacy Policy explains how we collect, use, and safeguard your personal data when you use our payment services in Indonesia, and other applicable jurisdictions.</p>

        <h3>2. Data Controller / Contact Info</h3>
        <ol>
          <li>PayCools Limited acts as the Data Controller for the purposes of the Perlindungan Data Pribadi (PDP) and other applicable data protection laws.</li>
          <li>If you have any questions or requests regarding your personal data, you may contact us at:<br>
            Email: [privacy@paycools.com]<br>
            Address: [Insert office address if applicable]<br>
            Data Protection Officer (DPO): [Insert name or position if appointed] - not necessary in law but recommend to do so
          </li>
        </ol>

        <h3>3. Types of Data Collected</h3>
        <p>When you use any of the following services, we collect your personal data:</p>
        <ol>
          <li>Our website</li>
          <li>Any of our applications</li>
          <li>Any services offered through our apps or website</li>
        </ol>

        <p>We may collect personal data directly from you, through your use of our services, from third-party sources (such as financial institutions, fraud prevention agencies, or public databases), or from other users. This includes, but is not limited to, the following categories:</p>

        <h4>(1) Information you provide to us</h4>
        <p>We collect information you provide when you:</p>
        <ul>
          <li>Fill in forms on our website or app</li>
          <li>Open an account or apply to use our services</li>
          <li>Complete identity verification (KYC) or compliance checks</li>
          <li>Correspond with us by phone, email, chat, or social media</li>
          <li>Respond to surveys or participate in promotions or competitions</li>
          <li>Provide information for customer support or dispute resolution</li>
          <li>Connect third-party accounts (e.g., bank accounts, wallets, payment cards) to our services</li>
        </ul>
        <p>This may include:</p>
        <ul>
          <li>Identification Data: Full name, date of birth, nationality, gender, copies of identification documents (passport, ID card, driver's license), facial recognition or biometric data used for identity verification.</li>
          <li>Contact Information: Residential and billing addresses, email address, phone number, emergency contact information.</li>
          <li>Financial Data: Bank account details (IBAN, sort code, SWIFT), payment card details (card number, expiry date, CVV), linked financial accounts, account balances, transaction history.</li>
          <li>Business Information (if you are a merchant): Company name, registration number, tax identification number, business licenses/permits, merchant category codes (MCC), ownership and control structure.</li>
          <li>Compliance Data: KYC/AML documents, proof of address, tax residency information, sanctions screening results, risk scores, source of funds/wealth information.</li>
          <li>Communications: Records of phone calls, chats, emails, or social media interactions with our support team.</li>
        </ul>

        <h4>(2) Information we collect from your use of our services</h4>
        <p>Whenever you interact with our products, we automatically collect:</p>
        <ul>
          <li>Technical Data: IP address, device identifiers (IMEI, MAC address), login credentials, operating system, browser type and version, mobile network information, time zone, device settings, VPN usage, and language settings.</li>
          <li>Usage Data: Details of your account activity, services you use, links you click, pages you visit, session times, and interactions within our app or website.</li>
          <li>Transaction Data: Incoming and outgoing payments, date, time, currency, amount, counterparties, merchant details, geolocation of transactions (merchant terminal/ATM location), device used, and payment method.</li>
          <li>Location Data: If enabled, we may collect geolocation information via GPS or other technologies for fraud prevention and service optimization.</li>
        </ul>

        <h4>(3) Information we obtain from third parties</h4>
        <p>We may receive information about you from trusted external sources, such as:</p>
        <ul>
          <li>Financial Institutions & Payment Partners: Bank account details, linked card information, settlement and chargeback data.</li>
          <li>Credit Reference & Fraud Prevention Agencies: Credit history, fraud risk scores, sanctions list checks, information on late payments, bankruptcy, or criminal records (where legally permitted).</li>
          <li>Regulators & Public Authorities: Identity or compliance confirmations as required by law.</li>
          <li>Publicly Available Sources: Online registers, company filings, sanctions lists, adverse media reports, and open government databases.</li>
          <li>Other Users: For example, if another customer identifies you as a beneficiary, payer, or business counterparty.</li>
        </ul>

        <h4>(4) Information from social media and online interactions</h4>
        <p>Occasionally, we may use publicly available information from social media platforms (LinkedIn, Facebook, Twitter, etc.) to conduct enhanced due diligence checks, confirm identity, or comply with anti-money laundering and sanctions requirements.</p>
        <p>If you engage with us on social media (by tagging us, posting on our pages, or participating in promotions), we may collect the information you share.</p>

        <h3>4. Purpose of Processing</h3>
        <p>We process personal data strictly for lawful and legitimate business purposes, including but not limited to:</p>
        <ol>
          <li>Service Provision – to register merchants, process payments, facilitate pay-ins and pay-outs, and provide customer support.</li>
          <li>Regulatory Compliance – to meet obligations under AML, CTF, tax, and financial services regulations.</li>
          <li>Risk Management & Fraud Prevention – to detect, investigate, and prevent fraud, money laundering, and other financial crimes.</li>
          <li>Contractual Necessity – to perform contractual obligations with merchants and ensure settlement accuracy.</li>
          <li>Communication – to send important service updates, legal notices, and respond to inquiries.</li>
          <li>Service Improvement – to enhance system performance, develop new products, and conduct analytics.</li>
          <li>Legal Claims – to establish, exercise, or defend legal claims where necessary.</li>
        </ol>

        <h3>5. Legal Basis</h3>
        <p>We process your personal data under the following legal bases, as permitted by Perlindungan Data Pribadi (PDP):</p>

        <h3>6. Sharing & Disclosure</h3>
        <p>We may share your personal data only in limited circumstances, as described below:</p>
        <ol>
          <li>Service Providers: We share information with trusted third-party service providers who perform functions on our behalf, such as payment processors, KYC/AML verification providers, fraud prevention tools, IT and cloud hosting services.</li>
          <li>Affiliates & Group Companies: We may share your data with our subsidiaries or affiliates, provided they adhere to this Privacy Policy.</li>
          <li>Regulators & Authorities: We may disclose information where required by law, regulation, or lawful request, including financial regulators, tax authorities, or law enforcement agencies.</li>
          <li>Business Transfers: In the event of a merger, acquisition, or sale of assets, your data may be transferred as part of the transaction, subject to the same protection standards.</li>
          <li>International Transfers: Where we transfer data outside the Indonesia, we ensure adequate safeguards are in place.</li>
          <li>With Your Consent: In specific cases, we may share data with third parties when you have provided explicit consent.</li>
        </ol>

        <h3>7. International Transfers</h3>
        <p>We may transfer and store your personal data outside the Indonesia, including to countries that may not provide the same level of data protection. Where we do so, we ensure that appropriate safeguards are in place, such as:</p>
        <ol>
          <li>Standard Contractual Clauses (SCCs) approved by relevant authorities;</li>
          <li>Transfers to jurisdictions deemed to provide adequate protection by the relevant authorities;</li>
          <li>Binding Corporate Rules (BCRs), where applicable.</li>
        </ol>
        <p>You may request a copy of the relevant safeguards by contacting us at the details provided in Section 2 above.</p>

        <h3>8. Data Retention</h3>
        <p>We retain your personal data only for as long as necessary to fulfill the purposes for which it was collected, including to comply with applicable legal, regulatory, accounting, or reporting obligations. The retention period depends on the type of data:</p>
        <ol>
          <li>Transaction Data: Retained for a minimum of 5–7 years to comply with anti-money laundering (AML), tax, and financial record-keeping obligations. Certain records may be retained longer if required by statutory limitation periods or regulatory authorities.</li>
          <li>Customer Support Records: Retained for up to 3 years following the closure of your account or your last interaction with us, in order to respond to potential claims or inquiries.</li>
          <li>Marketing Data: Retained until you withdraw your consent, opt out of receiving marketing communications, or after a period of 24 months of inactivity, whichever comes first.</li>
          <li>Technical and Security Logs: Retained for 12–24 months for system integrity, fraud detection, and security investigation purposes. Longer retention may apply where logs are relevant to an ongoing incident or investigation.</li>
          <li>Legal and Contractual Records: Where necessary, certain contracts, consents, and communications may be stored for the duration of the contractual relationship and up to the statutory limitation period.</li>
          <li>Data Minimization & Review: We periodically review the personal data we hold to ensure it is not kept longer than necessary. Where possible, we anonymize or aggregate data so that it can no longer identify you and may be used for statistical, analytical, or business purposes without further notice.</li>
          <li>Deletion: Once the applicable retention period expires, we will securely delete, anonymize, or irreversibly pseudonymize your data, unless we are legally required to retain it.</li>
        </ol>

        <h3>9. User Rights</h3>
        <p>Under applicable data protection laws, you have the following rights regarding your personal data. You may exercise these rights at any time by contacting us:</p>
        <ol>
          <li>Right of Access – You have the right to obtain confirmation as to whether we process your personal data and to request a copy of such data, along with information on how it is used.</li>
          <li>Right to Rectification – You may request correction of any inaccurate or incomplete personal data we hold about you.</li>
          <li>Right to Erasure ("Right to be Forgotten") – You may request deletion of your personal data where there is no longer a legitimate reason for us to retain it, subject to legal or regulatory obligations.</li>
          <li>Right to Restrict Processing – You may request that we restrict the processing of your personal data under certain circumstances, such as when the accuracy of the data is contested or when processing is unlawful.</li>
          <li>Right to Data Portability – You may request to receive your personal data in a structured, commonly used, and machine-readable format, and to have it transmitted to another controller where technically feasible.</li>
          <li>Right to Object – You may object to our processing of your personal data where the processing is based on legitimate interests, including direct marketing activities.</li>
          <li>Right to Withdraw Consent – Where processing is based on your consent, you have the right to withdraw that consent at any time, without affecting the lawfulness of prior processing.</li>
          <li>Right to Lodge a Complaint – You have the right to lodge a complaint with your local supervisory authority if you believe that your data protection rights have been violated.</li>
        </ol>
        <p>We will respond to all requests without undue delay and, in any event, within the time limits established by applicable data protection law.</p>

        <h3>10. Security Measures</h3>
        <p>We take the protection of your personal data seriously and implement appropriate technical and organizational measures to ensure its confidentiality, integrity, and availability. These include:</p>
        <ol>
          <li>Data Encryption: All personal data transmitted between you and our systems is encrypted using industry-standard encryption protocols (e.g., TLS/SSL).</li>
          <li>Access Controls: Access to personal data is strictly limited to authorized personnel with a legitimate business need, and subject to strict authentication procedures.</li>
          <li>Data Minimization & Pseudonymization: Wherever possible, we minimize the personal data collected and apply pseudonymization techniques to reduce risk.</li>
          <li>Monitoring & Testing: We conduct regular security monitoring, vulnerability scanning, and penetration testing of our systems.</li>
          <li>Incident Response: We maintain a documented incident response plan to detect, investigate, and respond to potential data breaches. In the event of a breach, we will notify affected users and relevant supervisory authorities in accordance with applicable law.</li>
          <li>Vendor Due Diligence: We require all third-party service providers who process personal data on our behalf to adhere to strict data protection and security standards.</li>
        </ol>

        <h3>11. Policy Updates</h3>
        <p>We may update this Privacy Policy from time to time to reflect changes in our practices, legal requirements, or regulatory guidance.</p>
        <ol>
          <li>Notification of Changes: When significant changes occur, we will notify users via email, in-app notification, or prominent notice on our website.</li>
          <li>Effective Date: The "Effective Date" of the latest version of this Privacy Policy is clearly stated at the top of the document.</li>
          <li>Your Continued Use: By continuing to use our services after updates are posted, you agree to the revised Privacy Policy. We encourage you to review this Policy periodically to stay informed about how we are protecting your personal data.</li>
        </ol>
      </div>
    </div>
    <span slot="footer" class="dialog-footer">
      <el-button type="text" style="background-color: #3855F1; color: #ffffff; width: 120px; padding: 12px; height: 34px" @click="handleClose">Close</el-button>
    </span>
  </el-dialog>
</template>

<script>
export default {
  name: 'PrivacyPolicy',
  props: {
    visible: {
      type: Boolean,
      default: false
    }
  },
  methods: {
    handleClose(done) {
      // emit update for v-model sync
      this.$emit('update:visible', false)
      if (typeof done === 'function') done()
    }
  }
}
</script>

<style scoped>
.privacy-policy-page {
  height: 560px;
  padding: 0;
  min-height: unset;
  background: transparent;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  line-height: 1.6;
  color: #1f3b6f;
}

.privacy-policy-page .container {
  max-width: 100%;
  margin: 0;
  background: #fff;
  border-radius: 6px;
  box-shadow: none;
  height: 100%;
  overflow-y: auto;
}

/* Dialog sizing/scroll behavior */
.privacy-dialog .el-dialog__body{
  padding: 0;
  box-sizing: border-box;
  overflow: hidden;
}
.privacy-dialog .container{
  height: 100%;
  overflow-y: auto;
}
.privacy-dialog .el-dialog__headerbtn .el-dialog__close {
  color: #3855F1;
  right: 16px;
}
.privacy-dialog .el-dialog__footer {
  padding: 12px 24px;
  text-align: right;
}

/* Main title */
.privacy-policy-page h1 {
  color: #3855F1;
  font-family: 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 22px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin: 8px 0 12px 0;
  text-align: left;
  border-bottom: 1px solid #e6f0ff;
  padding-bottom: 8px;
}

/* Subtitle */
.privacy-policy-page h2 {
  color: #1D3A71;
  font-family: 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 16px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
  margin: 6px 0 16px 0;
  text-align: left;
}

/* Section headers (h3) */
.privacy-policy-page h3 {
  font-size: 18px;
  font-weight: 700;
  color: #16325a;
  margin: 20px 0 12px 0;
  padding-bottom: 6px;
  border-bottom: 1px solid #eef6ff;
}

/* Subsection headers (h4) */
.privacy-policy-page h4 {
  font-size: 15px;
  font-weight: 600;
  color: #4a5568;
  margin: 18px 0 10px 0;
}

/* Paragraphs */
.privacy-policy-page p {
  color: #1D3A71;
  font-family: 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin: 0 0 12px 0;
  /* prevent breaking words across lines */
  word-break: keep-all;
  overflow-wrap: normal;
  hyphens: none;
}

/* Lists */
.privacy-policy-page ol {
  margin: 8px 0;
  padding-left: 30px;
}

.privacy-policy-page ul {
  margin: 8px 0;
  padding-left: 30px;
}

.privacy-policy-page li {
  color: #1D3A71;
  font-family: 'Inter Tight', -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  margin-bottom: 8px;
  /* prevent breaking words across lines */
  word-break: keep-all;
  overflow-wrap: normal;
  hyphens: none;
}

.privacy-policy-page li strong {
  /* removed strong tags - no special styling */
  color: inherit;
  font-weight: 400;
}

/* neutralize any remaining strong */
.privacy-policy-page strong {
  color: inherit;
  font-weight: 400;
}

/* Dialog title styling handled via slot inline styles */

/* Confirmation text */
.privacy-policy-page .confirmation-text {
  margin-top: 50px;
  padding: 30px;
  background: linear-gradient(135deg, #f7fafc 0%, #edf2f7 100%);
  border: 2px solid #4299e1;
  border-radius: 8px;
  text-align: center;
}

.privacy-policy-page .confirmation-text p {
  font-size: 18px;
  font-weight: 600;
  color: #2d3748;
  margin: 0;
}

</style>

